// Per-IP progressive lockout for failed API-key auth attempts, plus the Bearer
// credential classification that feeds it. Extracted from http.ts so the policy
// is unit-testable in isolation (http.ts binds a live server on import).
//
// TEN-2606: the lockout must penalize only a *presented-and-rejected* Bearer
// credential — never an *absent* one. Absent-credential /mcp calls are the
// spec-mandated OAuth bootstrap probe (the client hits the resource with no
// token to receive the 401 + WWW-Authenticate challenge). A client re-bootstraps
// on every connect, and in a burst right after a Railway restart wipes the
// in-memory token/session maps — so counting those probes as attacks self-locks
// a legitimate IP. resolveBearerAuth() distinguishes the two; the handlers only
// call recordAuthFailure() on the "rejected" branch.

export interface AuthFailureRecord {
  count: number;
  firstFailure: number;
  blockedUntil: number;
}

// Minimal shape of an opaque access-token store entry (http.ts's accessTokens).
export interface AccessTokenLike {
  apiKey: string;
  createdAt: number;
}

// Outcome of classifying a request's Authorization header.
//  - "ok":       a valid credential; apiKey is the resolved pb_sk_ key.
//  - "absent":   no Bearer credential was presented — never penalized.
//  - "rejected": a Bearer credential was presented but is invalid — penalized.
export type BearerAuth =
  | { status: "ok"; apiKey: string }
  | { status: "absent" }
  | { status: "rejected" };

export const AUTH_FAILURE_MAX = 10;
export const AUTH_FAILURE_WINDOW_MS = 5 * 60_000; // 5 minutes
export const AUTH_BLOCK_DURATION_MS = 15 * 60_000; // 15 minutes
export const MAX_AUTH_FAILURE_ENTRIES = 10_000;

/**
 * Classify a request's Authorization header into absent / rejected / ok.
 *
 * Supports both direct pb_sk_ API keys (stdio / backward compat) and opaque
 * pb_at_ OAuth access tokens issued by issueTokens(). Expired opaque tokens are
 * evicted from `accessTokens` as a side effect. `now` is injected (never
 * Date.now()) so callers and tests share a single clock.
 */
export function resolveBearerAuth(
  authorizationHeader: unknown,
  accessTokens: Map<string, AccessTokenLike>,
  now: number,
  accessTokenTtlMs: number,
): BearerAuth {
  if (typeof authorizationHeader !== "string" || authorizationHeader.trim() === "") {
    // No Authorization header at all — the OAuth bootstrap probe. This is the
    // ONLY case that escapes the lockout: a client hitting the resource with no
    // credential to fetch the 401 + WWW-Authenticate challenge.
    return { status: "absent" };
  }
  if (!authorizationHeader.startsWith("Bearer ")) {
    // A credential of a wrong scheme (e.g. Basic) was presented — it can never
    // authenticate, so treat it as a rejected attempt that accrues toward the
    // lockout. Classifying it "absent" would let a caller bypass the guard by
    // always sending a non-Bearer header.
    return { status: "rejected" };
  }
  const token = authorizationHeader.slice(7).trim();
  if (token === "") {
    // "Bearer " scheme presented with an empty token — a presented, unusable
    // credential, not the (header-less) bootstrap probe.
    return { status: "rejected" };
  }

  if (token.startsWith("pb_sk_")) {
    // Direct API key — accepted for stdio and backward compatibility.
    return { status: "ok", apiKey: token };
  }
  if (token.startsWith("pb_at_")) {
    // Opaque OAuth access token — resolve to the underlying API key.
    const entry = accessTokens.get(token);
    if (!entry) return { status: "rejected" };
    if (now - entry.createdAt > accessTokenTtlMs) {
      // Expired — evict and reject.
      accessTokens.delete(token);
      return { status: "rejected" };
    }
    return { status: "ok", apiKey: entry.apiKey };
  }
  // A Bearer credential of unknown shape was presented — treat as a rejected
  // attempt so brute-forcing garbage tokens still accrues toward the lockout.
  return { status: "rejected" };
}

/** True if `ip` is currently within an active lockout block. */
export function checkAuthBlock(
  failures: Map<string, AuthFailureRecord>,
  ip: string,
  now: number,
): boolean {
  const rec = failures.get(ip);
  if (!rec) return false;
  return rec.blockedUntil > now;
}

/**
 * Record one presented-and-rejected auth failure for `ip`. Callers MUST NOT
 * call this for an absent credential (TEN-2606). Tips the IP into a block once
 * AUTH_FAILURE_MAX failures land inside AUTH_FAILURE_WINDOW_MS; a failure past
 * the window resets the count rather than accumulating across windows.
 */
export function recordAuthFailure(
  failures: Map<string, AuthFailureRecord>,
  ip: string,
  now: number,
): void {
  const rec = failures.get(ip);

  if (!rec) {
    failures.set(ip, { count: 1, firstFailure: now, blockedUntil: 0 });
    return;
  }

  // Reset the window if the first failure is outside the tracking window.
  if (now - rec.firstFailure > AUTH_FAILURE_WINDOW_MS) {
    rec.count = 1;
    rec.firstFailure = now;
    rec.blockedUntil = 0;
  } else {
    rec.count++;
    if (rec.count >= AUTH_FAILURE_MAX) {
      rec.blockedUntil = now + AUTH_BLOCK_DURATION_MS;
    }
  }
}
